import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL } from '../../api/constant';
import { showToastNotification } from '../../utility/helper';
import Layout from '../../components/Layout/Layout';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import { ISettings } from '../../interfaces/settings';

interface SettingFormInterface {
    taxRate: number;
    deliveryFees: number;
    supportNumber: string;
}
const Settings: React.FC = () => {
    const [settings, setSettings] = useState<ISettings | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [freeDelivery, setFreeDelivery] = useState<boolean>(false);
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<SettingFormInterface>({ mode: 'onBlur' });

    // Watch deliveryFees to update freeDelivery state
    const deliveryFeesValue = watch('deliveryFees');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL + API_ENDPOINT.auth + 'settings', { method: 'GET' });
            if (response && response.length > 0) {
                let setting = response[0]
                setSettings(setting)
                setValue('taxRate', setting?.taxRate || 0);
                setValue('deliveryFees', setting?.deliveryFees || 0);
                setValue('supportNumber', setting?.supportNumber || '');
                setFreeDelivery(setting?.deliveryFees === 0);
            }

        } catch (error: any) {
            console.error('Failed to fetch settings:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit: SubmitHandler<SettingFormInterface> = async (data) => {
        setLoadingButton(true);
        try {
            // If free delivery is checked, set delivery fees to 0
            const submitData = {
                ...data,
                deliveryFees: freeDelivery ? 0 : data.deliveryFees
            };

            await callApi(API_URL + API_ENDPOINT.auth + 'settings/' + settings?.id, { method: 'PUT', data: submitData });
            showToastNotification('success', 'Settings updated successfully');
            fetchSettings();
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    const handleFreeDeliveryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setFreeDelivery(isChecked);

        // If free delivery is checked, set delivery fees to 0 and disable validation
        if (isChecked) {
            setValue('deliveryFees', 0);
        }
    };

    // Update freeDelivery state when deliveryFees becomes 0 through input
    useEffect(() => {
        if (deliveryFeesValue === 0) {
            setFreeDelivery(true);
        } else {
            setFreeDelivery(false);
        }
    }, [deliveryFeesValue]);

    if (loading) {
        return <Layout>Loading settings...</Layout>;
    }

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-start mb-4">
                <h1 className="app-page-title">Settings</h1>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row">
                                    {/* Tax Rate Section */}
                                    <div className="mb-3 col-md-6">
                                        <InputField
                                            label='Tax Rate (%)'
                                            type="text"
                                            register={register('taxRate', {
                                                required: 'Tax rate is required',
                                                pattern: {
                                                    value: /^\d+(\.\d+)?$/,
                                                    message: 'Only numbers are allowed'
                                                },
                                                validate: value => {
                                                    if (value <= 0) {
                                                        return 'Tax rate must be greater than 0';
                                                    }
                                                    if (value > 99) {
                                                        return 'Tax rate must be less than or equal to 99';
                                                    }
                                                    return true;
                                                }
                                            })}
                                            name="taxRate"
                                            placeholder="Enter tax rate"
                                        />
                                        <ErrorMessage message={errors.taxRate?.message?.toString()} />
                                    </div>

                                    {/* Customer Support Number Section */}
                                    <div className="mb-3 col-md-6">
                                        <InputField
                                            label='Customer Support Number'
                                            type="text"
                                            register={register('supportNumber', {
                                                required: 'Customer support number is required',
                                                pattern: {
                                                    value: /^[\+]?[1-9][\d]{0,15}$/,
                                                    message: 'Please enter a valid phone number'
                                                }
                                            })}
                                            name="supportNumber"
                                            placeholder="Enter customer support number"
                                        />
                                        <ErrorMessage message={errors.supportNumber?.message?.toString()} />
                                    </div>

                                    {/* Delivery Fees Section */}
                                    <div className="mb-3 col-md-6">
                                        <InputField
                                            label='Delivery Fees'
                                            type="text"
                                            register={register('deliveryFees', {
                                                required: freeDelivery ? false : 'Delivery fees is required',
                                                pattern: {
                                                    value: /^\d+(\.\d+)?$/,
                                                    message: 'Only numbers are allowed'
                                                },
                                                validate: value => {
                                                    if (freeDelivery) return true;
                                                    return value > 0 || 'Delivery fees must be greater than 0';
                                                }
                                            })}
                                            name="deliveryFees"
                                            placeholder="Enter delivery fees"
                                            disabled={freeDelivery}
                                        />
                                        <ErrorMessage message={errors.deliveryFees?.message?.toString()} />

                                        {/* Free Delivery Checkbox */}
                                        <div className="form-check mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="freeDelivery"
                                                checked={freeDelivery}
                                                onChange={handleFreeDeliveryChange}
                                            />
                                            <label className="form-check-label" htmlFor="freeDelivery">
                                                Free Delivery
                                            </label>
                                        </div>
                                    </div>

                                    {/* Submit Button Section */}
                                    <div className="col-12 text-end mt-3 ">
                                        <Button
                                            loading={loadingButton}
                                            className="btn btn-primary text-white px-5"
                                            type="submit"
                                            text="Update Settings"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

            </div>
        </Layout>
    );
};

export default Settings;